import { NgClass, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, TrackByFunction, inject } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxFor } from '@rx-angular/template/for';

import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroupTrackingService } from './option-group-tracking.service';
import { OptionGroupService } from './option-group.service';
import { OptionComponent } from './option.component';

// header is always 40px
// each optionGroup is always 44px height
const headerHeight = 40;
const optionGroupHeight = 44;
const containerPadding = 14;

@Component({
    selector: 'ms-score-market-option-group',
    templateUrl: './score-market-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MarketHelpComponent, NgClass, RxFor, OptionComponent, SlicePipe, IconCustomComponent],
})
export class ScoreMarketOptionGroupComponent implements OnChanges {
    private optionGroupService = inject(OptionGroupService);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    sitecore = inject(Sitecore);

    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() groupType: OptionGroupType;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;

    numberOfOptions: number;
    tupleClass: string;

    private optionGroupTrackingService = inject(OptionGroupTrackingService);

    expanded = false;
    optionHeaders: string[];
    optionGroups: EventOption[][];
    showMoreLessButtonVisible = false;
    visibleOptionsLimit: number;
    minContainerHeight: number;
    threshold: number;

    gridClasses = new Map<number, string>([
        [2, 'double'],
        [3, 'triple'],
        [4, 'quadruple'],
        [5, 'quintuple'],
        [6, 'sextuple'],
    ]);

    ngOnChanges(): void {
        this.optionGroups = this.optionGroupService.getScoreMarketGroups(this.optionGroup.options);
        this.calculateMaxNumberOfVisibleOptions();
    }

    trackOption: TrackByFunction<EventOption> = (index, option) => option.id;
    trackHeader: TrackByFunction<string> = (index, header) => header;
    trackGroup: TrackByFunction<EventOption[]> = (index) => index;

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;
        this.calculateMaxNumberOfVisibleOptions();

        this.optionGroupTrackingService.trackCollapseExpandClick(
            this.event.sport.name,
            this.expanded ? 'expand' : 'collapse',
            this.event.participants,
            this.optionGroup.name,
            this.optionGroup.name,
        );
    }

    getOption(group: EventOption[], header: string): EventOption | undefined {
        return (
            group.find((opt) => opt.name === header) || group.find((opt) => opt.name !== this.optionHeaders[0] && opt.name !== this.optionHeaders[1])
        );
    }

    private setModel(): void {
        this.numberOfOptions = this.optionGroups[0].length;
        this.tupleClass = this.getTupleClass();
        switch (this.groupType) {
            case OptionGroupType.RaceTo:
                this.optionHeaders = this.event.participants.map((value) => value.shortName || value.name || '');
                this.threshold = this.optionsLimitOverride ?? this.marketGroupingConfig.optionGroupRowsThreshold.raceTo;
                if (this.numberOfOptions === 3) {
                    this.optionHeaders.push(this.sitecore.event.Neither);
                }
                break;
            case OptionGroupType.BTTS:
                this.optionHeaders = [...new Set(this.optionGroup.options.map((value) => value.name || ''))];
                this.threshold = this.optionsLimitOverride ?? this.marketGroupingConfig.optionGroupRowsThreshold.btts;
                break;
            default:
                break;
        }
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.setModel();
        this.showMoreLessButtonVisible = this.optionGroups.length > this.threshold;
        this.visibleOptionsLimit = this.expanded ? this.optionGroups.length : this.threshold;
        this.minContainerHeight = this.optionGroups.slice(0, this.visibleOptionsLimit).length * optionGroupHeight + headerHeight + containerPadding;
    }

    private getTupleClass(): string {
        return this.gridClasses.get(this.numberOfOptions + 1) ?? '';
    }
}
